import { useState, useEffect } from 'react';
import { useRouter } from 'next/router';
import { useDIContext } from '@/Framework/DI/DIContext';
import StratsRepository from '@/finsight/infrastructure/repository/StratsRepository/StratsRepository';
import { IGroup, IStrats } from '@/finsight/infrastructure/repository/StratsRepository/types';
import { getErrorMessage } from '@/Framework/Message/Mapper/getMessage';
import { AlertManager } from '@dealroadshow/uikit/core/components/Alert';
import { TFileId } from '@/finsight/domain/strats/types/SidebarStrats';

interface IuseStratsTableProps {
  rangeExcludedIds: number[],
  initialGroups: IGroup[],
  initialGroupedStrats: IStrats,
}

export const useStratsTable = ({
  rangeExcludedIds,
  initialGroups,
  initialGroupedStrats,
}: IuseStratsTableProps) => {
  const stratsRepository = useDIContext().container.get(StratsRepository);
  const { query: { fileId } } = useRouter();
  const [groupedStrats, setGroupedStrats] = useState<IStrats>(initialGroupedStrats);
  const [isStratsFetching, setIsStratsFetching] = useState<boolean>(false);
  const [groups, setGroups] = useState<IGroup[]>(initialGroups);

  const getGroups = async (fileId: TFileId) => {
    try {
      const response = await stratsRepository.getGroups(fileId);
      setGroups(response.groups);
    } catch (error) {
      AlertManager.error(getErrorMessage(error));
    }
  };

  useEffect(() => {
    if (groups.length) {
      getStratsByGroupsIds(fileId, groups.map((x: IGroup) => x.groupId), rangeExcludedIds);
    }
  }, [groups]);

  const getStratsByGroupsIds = async (
    fileId: TFileId, groupIds: number[], rangeExcludedIds: number[] = [],
  ) => {
    setIsStratsFetching(true);
    try {
      const response = await stratsRepository.getStratsByGroupIds(fileId, groupIds, rangeExcludedIds);
      setGroupedStrats(response);
    } catch (error) {
      AlertManager.error(getErrorMessage(error));
    } finally {
      setIsStratsFetching(false);
    }
  };

  return {
    isStratsFetching,
    groups,
    groupedStrats,
    getGroups,
  };
};
